// src/components/digital_twin/twin_engine/ui/ui.js
// Shared building blocks for the asset twin drawer: readable type scale (min 11 px), FlowSense AI
// gradient accents, tokens for light / dark, and a dependency-free SVG trend chart with hover.
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Badge, Box, Flex, HStack, Icon, Text, Tooltip, useColorModeValue } from '@chakra-ui/react';

export const AI_GRADIENT = 'linear(to-r, cyan.400, blue.500, purple.500)';

export function useTwinTokens() {
  return {
    bg: useColorModeValue('gray.50', 'gray.900'),
    card: useColorModeValue('white', 'gray.800'),
    raised: useColorModeValue('gray.50', 'whiteAlpha.50'),
    border: useColorModeValue('gray.200', 'whiteAlpha.200'),
    accentBorder: useColorModeValue('cyan.200', 'cyan.800'),
    text: useColorModeValue('gray.800', 'gray.100'),
    muted: useColorModeValue('gray.600', 'gray.400'),
    faint: useColorModeValue('gray.500', 'gray.500'),
    hover: useColorModeValue('gray.100', 'whiteAlpha.100'),
    line: useColorModeValue('#2B6CB0', '#63B3ED'),
    grid: useColorModeValue('#E2E8F0', 'rgba(255,255,255,0.08)'),
    surface: useColorModeValue('#FFFFFF', '#1A202C'),
  };
}

export const Section = memo(({ title, icon, right, children, accent = false, ...rest }) => {
  const t = useTwinTokens();
  return (
    <Box bg={t.card} borderWidth="1px" borderColor={accent ? t.accentBorder : t.border} borderRadius="xl" p={4} position="relative" overflow="hidden" {...rest}>
      {accent && <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient={AI_GRADIENT} />}
      {(title || right) && (
        <Flex justify="space-between" align="center" mb={3} gap={2}>
          <HStack spacing={2} minW={0}>
            {icon && <Icon as={icon} color="cyan.400" boxSize={3.5} />}
            <Text fontSize="sm" fontWeight="800" color={t.text} noOfLines={1}>{title}</Text>
          </HStack>
          {right}
        </Flex>
      )}
      {children}
    </Box>
  );
});
Section.displayName = 'Section';

export const Stat = memo(({ label, value, sub, color, hint }) => {
  const t = useTwinTokens();
  const body = (
    <Box minW={0}>
      <Text fontSize="11px" fontWeight="700" color={t.muted} textTransform="uppercase" letterSpacing="0.04em" noOfLines={1}>{label}</Text>
      <Text fontSize="xl" fontWeight="900" fontFamily="mono" color={color || t.text} lineHeight="1.2">{value ?? '—'}</Text>
      {sub && <Text fontSize="11px" color={t.muted} noOfLines={1}>{sub}</Text>}
    </Box>
  );
  return hint ? <Tooltip label={hint} hasArrow openDelay={300}>{body}</Tooltip> : body;
});
Stat.displayName = 'Stat';

export const KV = memo(({ label, value, color }) => {
  const t = useTwinTokens();
  return (
    <Flex justify="space-between" gap={3} py={1.5} borderBottomWidth="1px" borderColor={t.border} _last={{ borderBottomWidth: 0 }}>
      <Text fontSize="13px" color={t.muted} flexShrink={0}>{label}</Text>
      <Text fontSize="13px" fontWeight="600" color={color || t.text} textAlign="right" wordBreak="break-word">{value ?? '—'}</Text>
    </Flex>
  );
});
KV.displayName = 'KV';

export const Empty = memo(({ icon, title, children }) => {
  const t = useTwinTokens();
  return (
    <Flex direction="column" align="center" justify="center" textAlign="center" py={8} px={4} gap={2} color={t.muted}>
      {icon && <Icon as={icon} boxSize={6} opacity={0.6} />}
      <Text fontSize="sm" fontWeight="700" color={t.text}>{title}</Text>
      {children && <Text fontSize="13px" maxW="420px">{children}</Text>}
    </Flex>
  );
});
Empty.displayName = 'Empty';

const LEVEL_SCHEME = { critical: 'red', high: 'orange', watch: 'yellow', warning: 'orange', info: 'blue', ok: 'green', stable: 'green', urgent: 'red', medium: 'yellow', low: 'gray' };
export const LevelBadge = ({ level, children, ...rest }) => (
  <Badge colorScheme={LEVEL_SCHEME[level] || 'gray'} variant="subtle" fontSize="10px" borderRadius="md" px={1.5} {...rest}>{children ?? level}</Badge>
);

// Date-only values (inspection dates) are calendar days — parse them as local, not UTC midnight.
const toMs = (d) => (typeof d === 'number' ? d : d instanceof Date ? d.getTime() : /^\d{4}-\d{2}-\d{2}$/.test(String(d)) ? Date.parse(`${d}T00:00:00`) : Date.parse(d));
export const fmtDateTime = (d) => { const x = toMs(d); return Number.isFinite(x) ? new Date(x).toLocaleString() : '—'; };
export const fmtDate = (d) => { const x = toMs(d); return Number.isFinite(x) ? new Date(x).toLocaleDateString() : '—'; };
export const ago = (ts) => {
  const x = typeof ts === 'number' ? ts : Date.parse(ts);
  if (!Number.isFinite(x)) return '—';
  const s = Math.round((Date.now() - x) / 1000);
  if (s < 60) return `${Math.max(0, s)} s ago`; if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`; return `${Math.round(s / 86400)} d ago`;
};

/**
 * Single-series trend (line + soft area) with a hover crosshair and tooltip.
 * points: [{ ts, value }] sorted by ts. Optional `bands` [{ from, to, color }] in value units,
 * `domain` [min, max], `format` for tooltip values, `xFormat` / `xTip` for the x axis (default: dates).
 */
export const TrendChart = memo(({ points = [], height = 120, domain, format = (v) => v, unit = '', label = 'Trend', bands = [], color, xFormat = fmtDate, xTip = fmtDateTime }) => {
  const t = useTwinTokens();
  const ref = useRef(null);
  const [hover, setHover] = useState(null);
  const W = 600; const H = height; const PAD = { l: 34, r: 8, t: 8, b: 18 };
  const geo = useMemo(() => {
    if (points.length < 2) return null;
    const xs = points.map((p) => p.ts); const vs = points.map((p) => p.value);
    const x0 = Math.min(...xs); const x1 = Math.max(...xs);
    let [y0, y1] = domain || [Math.min(...vs), Math.max(...vs)];
    if (y0 === y1) { y0 -= 1; y1 += 1; }
    const sx = (x) => PAD.l + ((x - x0) / Math.max(1, x1 - x0)) * (W - PAD.l - PAD.r);
    const sy = (v) => PAD.t + (1 - (v - y0) / (y1 - y0)) * (H - PAD.t - PAD.b);
    const d = points.map((p, i) => `${i ? 'L' : 'M'}${sx(p.ts).toFixed(1)},${sy(p.value).toFixed(1)}`).join('');
    const area = `${d}L${sx(x1).toFixed(1)},${H - PAD.b}L${sx(x0).toFixed(1)},${H - PAD.b}Z`;
    return { sx, sy, d, area, x0, x1, y0, y1 };
  }, [points, domain, H]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMove = useCallback((e) => {
    if (!geo || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * W;
    let best = 0; let bd = Infinity;
    points.forEach((p, i) => { const dd = Math.abs(geo.sx(p.ts) - x); if (dd < bd) { bd = dd; best = i; } });
    setHover(best);
  }, [geo, points]);

  if (!geo) return <Text fontSize="13px" color={t.muted}>Not enough history to draw a trend.</Text>;
  const stroke = color || t.line;
  const hp = hover != null ? points[hover] : null;
  const ticks = [geo.y0, (geo.y0 + geo.y1) / 2, geo.y1];
  return (
    <Box position="relative">
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label={label}
        onMouseMove={onMove} onMouseLeave={() => setHover(null)} style={{ display: 'block', cursor: 'crosshair' }}>
        {bands.map((b) => (
          <rect key={`${b.from}-${b.to}`} x={PAD.l} width={W - PAD.l - PAD.r} y={geo.sy(Math.min(geo.y1, b.to))}
            height={Math.max(0, geo.sy(Math.max(geo.y0, b.from)) - geo.sy(Math.min(geo.y1, b.to)))} fill={b.color} opacity="0.08" />
        ))}
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PAD.l} x2={W - PAD.r} y1={geo.sy(v)} y2={geo.sy(v)} stroke={t.grid} strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <text x={PAD.l - 4} y={geo.sy(v) + 3} textAnchor="end" fontSize="10" fill="currentColor" opacity="0.6">{Math.round(v)}</text>
          </g>
        ))}
        <path d={geo.area} fill={stroke} opacity="0.12" />
        <path d={geo.d} fill="none" stroke={stroke} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        {hp && (
          <g>
            <line x1={geo.sx(hp.ts)} x2={geo.sx(hp.ts)} y1={PAD.t} y2={H - PAD.b} stroke={stroke} strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
            <circle cx={geo.sx(hp.ts)} cy={geo.sy(hp.value)} r="4" fill={stroke} stroke={t.surface} strokeWidth="2" vectorEffect="non-scaling-stroke" />
          </g>
        )}
      </svg>
      <Flex justify="space-between" fontSize="10px" color={t.faint} pl="34px"><Text>{xFormat(geo.x0)}</Text><Text>{xFormat(geo.x1)}</Text></Flex>
      {hp && (
        <Box position="absolute" top={0} right={0} bg={t.card} borderWidth="1px" borderColor={t.border} borderRadius="md" px={2} py={1} boxShadow="sm" pointerEvents="none">
          <Text fontSize="12px" fontWeight="800" fontFamily="mono" color={t.text}>{format(hp.value)}{unit}</Text>
          <Text fontSize="10px" color={t.muted}>{xTip(hp.ts)}</Text>
        </Box>
      )}
    </Box>
  );
});
TrendChart.displayName = 'TrendChart';

/** Tiny inline sparkline (no hover) for dense lists. */
export const Sparkline = memo(({ points = [], width = 120, height = 32, color }) => {
  const t = useTwinTokens();
  if (points.length < 2) return null;
  const vs = points.map((p) => p.value); const lo = Math.min(...vs); const hi = Math.max(...vs) || lo + 1;
  const x0 = points[0].ts; const x1 = points[points.length - 1].ts;
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${(((p.ts - x0) / Math.max(1, x1 - x0)) * width).toFixed(1)},${(height - 2 - ((p.value - lo) / Math.max(1e-9, hi - lo)) * (height - 4)).toFixed(1)}`).join('');
  return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true"><path d={d} fill="none" stroke={color || t.line} strokeWidth="2" strokeLinejoin="round" /></svg>;
});
Sparkline.displayName = 'Sparkline';
